import { Component, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '@shared/services/auth.service';

import { FormsModule, NgForm } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToastrService } from '@shared/services/toastr.service';
import {
  EXTERNAL_RFC_5424_SYSLOG_LINK,
  STATIC_LINK_OGO_CA_CERT,
  STATIC_LINK_OGO_LOG_EXAMPLE,
} from 'app/app-routing.constants';
import { HttpClient } from '@angular/common/http';
import { ConfirmService } from '@shared/services/confirm.service';
import { ExportLogsService } from '@shared/services/export-logs/export-logs.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgIf, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { JsonViewer } from '@shared/components/json-viewer/json-viewer.component';
import { LabelComponent } from '@shared/components/label/label.component';
import {
  OrganizationPushLogService,
  PushLogConfig,
  PushLogConfigType,
  PushLogConfigTypes,
} from '@shared/services/organization-push-log.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-push-log',
  templateUrl: './push-log.component.html',
  styleUrls: ['./push-log.component.scss'],
  imports: [
    HelpComponent,
    NgIf,
    JsonViewer,
    FormsModule,
    NgTemplateOutlet,
    LabelComponent,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class PushLogComponent implements OnInit {
  lang: string;

  STATIC_LINK_OGO_CA_CERT = STATIC_LINK_OGO_CA_CERT;
  STATIC_LINK_OGO_LOG_EXAMPLE = STATIC_LINK_OGO_LOG_EXAMPLE;
  EXTERNAL_RFC_5424_SYSLOG_LINK = EXTERNAL_RFC_5424_SYSLOG_LINK;

  isLoading = true;
  statsError = false;
  stats = null;

  step: Step = 'LOADING';
  currentConfig: PushLogConfig;
  exportType: PushLogConfigType;
  pushLogConf: Record<PushLogConfigType, any> = {
    tcp: {
      host: null,
      port: null,
      sslEnabled: false,
    },
    sekoia: {
      intakeKey: null,
    },
    splunk: {
      url: null,
      hecToken: null,
      sslVerificationMode: 'full',
    },
    microsoftSentinelv2: {
      clientAppId: null,
      clientAppSecret: null,
      tenantId: null,
      dataCollectionEndpoint: null,
      dcrImmutableId: null,
      dcrStreamName: null,
    },
    syslog: {
      appName: null,
      host: null,
      port: null,
      protocol: null,
      sslEnabled: false,
    },
  };
  logsOutgoingIps: string = '';
  @ViewChild('profileForm') public editProfileForm: NgForm;

  constructor(
    private toastr: ToastrService,
    public auth: AuthService,
    private translate: TranslateService,
    private http: HttpClient,
    private organizationService: OrganizationPushLogService,
    private confirmService: ConfirmService,
    private exportLogsService: ExportLogsService,
  ) {
    this.lang = this.auth.getCurrentLanguage();
  }

  ngOnInit() {
    this.organizationService.getPushLog()
      .subscribe(pushLog => {
        if (pushLog?.configuration) {
          this.currentConfig = pushLog;
          this.parseConf(pushLog);
          this.getStats();
          this.step = 'CONFIGURED';
        } else {
          this.step = 'NOT_CONFIGURED';
        }
      })
    this.exportLogsService
      .getLogsOutgoingIpsAsObservable()
      .subscribe((value) => (this.logsOutgoingIps = value.join(', ')));
  }

  async getStats() {
    this.statsError = false;
    this.isLoading = true;
    this.stats = null;
    let res: any = null;
    try {
      res = await firstValueFrom(this.organizationService.getPushLogStats());
    } catch (e) {
      this.statsError = true;
    }
    this.stats = res;
    this.isLoading = false;
  }

  async saveUpdatePushLogConf() {
    let data: PushLogConfig;
    switch (this.exportType) {
      case 'syslog':
        let { sslEnabled, ...rest } = this.pushLogConf.syslog;
        data = {
          type: 'syslog',
          configuration: JSON.stringify({
            ...rest,
            protocol: sslEnabled ? 'ssl-tcp' : 'tcp',
          }),
        };
        break;
      default:
        data = {
          type: this.exportType,
          configuration: JSON.stringify(this.pushLogConf[this.exportType]),
        };
        break;
    }

    this.organizationService.setPushLog(data).subscribe(() => {
      this.toastr.success();
      this.currentConfig = data;
      this.step = 'CONFIGURED';
      this.parseConf(data);
    });
  }

  async deletePushLogConf() {
    this.organizationService.deletePushLog().subscribe(()=> {
      this.toastr.success();
      this.currentConfig = undefined;
      this.step = 'NOT_CONFIGURED';
      this.stats = null;
    });
  }

  triggerDeletePushLogConf() {
    this.confirmService.confirm({ html: this.translate.instant('DeletePushLogAlert') }, () => this.deletePushLogConf());
  }

  triggerModifyPushLogConf() {
    this.confirmService.confirm({ html: this.translate.instant('ModifyPushLogConf') }, () =>
      this.saveUpdatePushLogConf(),
    );
  }

  parseConf(configuration: PushLogConfig) {
    const c = JSON.parse(configuration.configuration);
    const t = configuration.type;
    // Order conf attributes alphabetically
    // Have to pass from a tmp variable, otherwise angular does not refresh
    let parsedConf = {};
    for (const key of Object.keys(c).sort()) {
      if (key == 'protocol' && t == 'syslog') {
        parsedConf['sslEnabled'] = c[key] == 'ssl-tcp';
        continue;
      }
      parsedConf[key] = c[key];
    }

    this.exportType = t;
    this.pushLogConf[t] = parsedConf;
  }

  selectLogExportType(type) {
    this.exportType = type;
    this.step = 'EDITING';
  }

  protected readonly PushLogConfigTypes = PushLogConfigTypes;

  protected readonly compats: Partial<Record<PushLogConfigType, string>> = {
    syslog: 'QRadar, Cortex, Logpoint, Wazuh'
  }
}


type Step = 'LOADING' | 'CONFIGURED' | 'NOT_CONFIGURED' | 'EDITING';
